import * as d3 from 'd3';
import { hashCodeFromSeat, safeNumber } from './helpers';

export const drawSeats = (location, seats,current_text, mode,zoom_key,stage,scales,simulation) => {
  const svg = d3.select(stage?.current);
  const duration = 600;
  
  const g = svg.selectAll("#wrapper");
  const zoom = d3.zoom().on("zoom", e => g.attr("transform", e.transform));
  g.call(zoom);
  const simNodes = seats.map(d => ({
  ...d,
  x: safeNumber(d.x, d.target_x),
  y: safeNumber(d.y, d.target_y),
  size: safeNumber(d.size, 20),
  }));
  const overlay = svg.select("#overlay-text");

  if (current_text !== undefined) {
    g.transition().duration(duration)
      .call(zoom.transform, d3.zoomIdentity.translate(570, -3000).scale(0.5));
    svg.selectAll("#nodes, #scales, #bg-graphics")
      .transition().duration(duration)

    overlay
      .data([current_text])
      .attr("x", 1500)
      .attr("y", 1600)
      .style("opacity",1)
      .text(d => d)
    return;
  } else {
    overlay.transition().duration(duration).style("opacity",0);
  }

  d3.select("#bg-graphics")
    .transition()
    .ease(d3.easeQuad)
    .duration(duration)
    .attr("opacity", zoom_key === "zoomed_out" ? 1 : 0.1);

  const states = {
    zoomed_out: d3.zoomIdentity.translate(570,1505).scale(0.5),
    zoomed_in: d3.zoomIdentity.translate(235,420).scale(0.85)
  };

  g.transition()
    .duration(duration)
    .delay(duration)
    .call(zoom.transform, states[zoom_key]);

  if (simulation.current) simulation.current.stop();
  simulation.current = d3.forceSimulation(simNodes)
    .force("x", d3.forceX(d => d.x).strength(0.1))
    .force("y", d3.forceY(d => d.y).strength(0.1))
    .velocityDecay(0.1)
    .alpha(1)
    .force("collide", d3.forceCollide().radius(d => d.size / 2 + 5).strength(1))
    .stop()
    .tick(150) 

  // Animation helpers
  const grow = (fn) => x => x
    .call(fn(false))
    .transition().duration(duration)
    .call(fn(true));

  const transitioned = (fn) => x => x
    .transition().duration(duration)
    .call(fn(true));

  const shrink = fn => x => x
    .transition().duration(duration)
    .call(fn(false))
    .remove();

  const size_and_pos = show => x => {
    x
      .attr("x", d => d.x + (show ? ((40-d.size)/2) : 20))
      .attr("y", d => d.y + (show ? ((40-d.size)/2) : 20))
      .attr("width", d => show ? d.size : 0)
      .attr("height", d => show ? d.size : 0)
      .attr("rx", d => show ? d.cr : 2)
      .attr("ry", d => show ? d.cr : 2)
      .attr("fill", d => d.color)
      .attr("opacity", d => d.opacity);
  };
  // Render nodes
  const nodes = svg.select('#nodes').selectAll("rect").interrupt()
    .data(simNodes, d => d.key)
    .join(
      enter => enter.append("rect").call(grow(size_and_pos)),
      update => update.call(transitioned(size_and_pos)),
      exit => exit.call(shrink(size_and_pos))
    )
    
nodes.selectAll("title")
     .data(d => [d])
     .join("title")
     .text(d => d.key);

  const scalesG = svg.select("#scales")
    .selectAll("g.scale")
    .data((scales || []).map((c,i) => ({ ...c, 
      lines:c.lines.map(x => ({...x,parent_i: i})),
      params:c.params.map(x => ({...x,parent_i: i})) 
    })), d => d.location + d.id[0])
    .join("g")
    .attr("class", "scale")
  scalesG.interrupt()    
    .transition().duration(duration)
    .attr("transform", (d, i) =>
      d.location === "top" ? `translate(0,${d.nth * 100-70})` : `translate(${d.nth * 100},0)`
    );
  const scale_bar = show => x => {
    x
      .attr("x", d => d.x)
      .attr("y", d => d.y)
      .attr("width", d => show ? d.width : 0)
      .attr("height", d => show ? d.height : 0)
      .attr("fill", d => d.fill);
  }
  scalesG
    .selectAll("rect").interrupt()
    .data(d => d.params,x => x.key)
    .join(
      enter => enter.append("rect").call(grow(scale_bar)),
      update => update.call(transitioned(scale_bar)),
      exit => exit.call(shrink(scale_bar))
    )

  const separating_line = show => x => {
    x
    .attr("x1", x =>  (x.location === "left" && show) ? x.x1+x.parent_i*-50 : x.centerX)
    .attr("x2", x =>  (x.location === "left" && show) ? x.x2+x.parent_i*-50 : x.centerX)
    .attr("y1", x =>  (x.location === "top" && show) ? x.y1+x.parent_i*-50 : x.centerY)
    .attr("y2", x =>  (x.location === "top" && show) ? x.y2+x.parent_i*-50 : x.centerY)
    .attr("stroke", "#ccc")
    .attr("stroke-width", 5)
    .attr("stroke-dasharray", "40,20")
  }
  scalesG
    .selectAll("line").interrupt()
    .data(d => (!d.is_col && mode === "grid") ? d.lines : [])
    .join(
      enter => enter.append("line").call(grow(separating_line)),
      update => update.call(transitioned(separating_line)),
      exit => exit.call(shrink(separating_line))
    )
    .attr("fill", d => d.fill);

  const grid_label = show => x => {
    x
    .attr("opacity",show ? 1 : 0)
    .attr("x", d => d.location === "left" ? d.text_x : d.text_x-d.parent_i*-50)
    .attr("y", d => d.location === "top" ? d.text_y : d.text_y)
  }
  scalesG
    .selectAll(".gridtext").interrupt()
    .data(d => (!d.is_col && mode === "grid") ? d.params : [])
    .join(
      enter => enter.append("text").attr("class","gridtext").call(grow(grid_label)),
      update => update.call(transitioned(grid_label)),
      exit => exit.call(shrink(grid_label))
    )
    .text(d => d.label_text)
    .style("font-size", "4dvh")
    .style("font-weight", "bold")
    .attr("fill", d => d.location === "top" ? "#aaa" : "black")
    .attr("text-anchor",  d => d.location === "top" ? "end" : "middle")
    .attr("transform", d => d.location === "top" ? 
      `rotate(270, ${d.text_x - 25}, ${d.text_y})` : `rotate(270, ${d.text_x-200}, ${d.text_y})`)

  const scale_text = show => x => {
    x
    .attr("opacity",show ? 1 : 0)
    .attr("x", d => d.location === "left" ? d.x - 25 : d.x + d.width / 2)
    .attr("y", d => d.location === "top" ? d.y - 25 : d.y + d.height / 2)
  }
  scalesG
    .selectAll(".labeltext").interrupt()
    .data(d => d.params, x => x.key)
    .join(
      enter => enter.append("text").attr("class","labeltext").call(grow(scale_text)),
      update => update.call(transitioned(scale_text)),
      exit => exit.call(shrink(scale_text))
    )
    .text(d => d.label)
    .style("font-size", "4dvh")
    .style("font-weight", "bold")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", d => d.location === "top" ? "baseline" : "middle")
    .attr("transform", d => d.location === "left" 
        ? `rotate(270, ${d.x - 25}, ${d.y + d.height / 2})` : null
    )
};

